import { ClipboardModule } from '@angular/cdk/clipboard'
import { HttpClient } from '@angular/common/http'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { AuthService } from '@auth0/auth0-angular'
import { SnackbarService } from '@saniup-webapps/shared'
import { SupportCardComponent } from '../../components/support-card/support-card.component'
import { OutlinedCardComponent } from '@saniup-webapps/shared/components'
import { MatCardModule } from '@angular/material/card'
import { ENV_TOKEN } from '../../app.config'
import { Employee } from '../../model/inko-backend.model'
import { EmployeeService } from '../../services/employee.service'
import { ADMIN_REITER } from '../../admin-page/admin-reiter'

/** Abstand zwischen zwei automatischen Pruefungen. */
const PRUEF_INTERVALL_MS = 20_000

/**
 * Nach 20 Minuten hoert die automatische Pruefung auf. Wer den Bildschirm offen liegen laesst, soll
 * nicht bis zum Feierabend alle 20 Sekunden abfragen; der Knopf bleibt.
 */
const MAX_AUTOMATISCHE_RUNDEN = 60

/**
 * Der Bildschirm fuer ein Konto **ohne Rolle** — der haeufigste Erstkontakt mit SaniUp ONE, den
 * niemand geplant hat.
 *
 * <p>Warum es ihn gibt: das Backend legt einen {@code SanitaetshausEmployee} beim ersten Anmelden
 * selbst an ("JIT-Provisioning"), und zwar mit {@code roles: []}. Bis eine Kollegin oder ein Kollege
 * die Rolle vergibt, darf dieses Konto nichts — die Seitenleiste waere leer, jeder Klick liefe ins
 * Nichts. Vorher stand hier ein einzelner Satz („Sie sind nicht freigeschaltet. Bitte kontaktieren
 * Sie den Administrator.") mit einem Logout-Knopf: fachlich richtig, praktisch eine Sackgasse — die
 * Sanitaetshaeuser wissen von sich aus weder, WER freischaltet, noch WO.
 *
 * <p>Der Bildschirm nimmt dem Nutzer deshalb beides ab: er nennt die drei Schritte, liefert den
 * fertigen Link in die Benutzerverwaltung zum Weiterschicken — und prueft selbst nach, damit
 * niemand raten muss, wann es so weit ist.
 */
@Component({
  selector: 'app-zugang-ausstehend',
  imports: [MatButtonModule, MatIconModule, MatCardModule, ClipboardModule, OutlinedCardComponent, SupportCardComponent],
  templateUrl: './zugang-ausstehend.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './zugang-ausstehend.component.scss',
})
export class ZugangAusstehendComponent {
  private readonly http = inject(HttpClient)
  private readonly apiUrl = inject(ENV_TOKEN).api.leistungserbringerUrl
  private readonly snackbarService = inject(SnackbarService)
  private readonly authService = inject(AuthService)
  readonly employeeService = inject(EmployeeService)

  readonly email = computed(() => {
    const employee = this.employeeService.currentlyLoggedInEmployee
    return employee.hasValue() ? employee.value().email : ''
  })

  /**
   * Der Link zeigt auf den Reiter „Benutzer:innen" der Admin-Seite ({@code admin-reiter.ts}). Absolut ueber {@code location.origin}, weil er zum
   * Verschicken gedacht ist und in jeder Stage stimmen muss.
   */
  readonly benutzerverwaltungLink = `${window.location.origin}/admin?tab=${ADMIN_REITER.BENUTZER}`

  /** Fertige Nachricht zum Weiterschicken — ein Klick statt selbst formulieren. */
  readonly nachricht = computed(
    () =>
      'Hallo,\n\n' +
      'ich habe mich gerade zum ersten Mal in SaniUp ONE angemeldet, mein Zugang ist aber noch nicht freigeschaltet.\n' +
      'Kannst du mir bitte die Rolle "Leistungserbringer" geben?\n\n' +
      `Benutzerverwaltung: ${this.benutzerverwaltungLink}\n` +
      `Mein Konto: ${this.email()}\n\n` +
      'Danke!',
  )

  /**
   * Eigene Abfrage statt {@code employeeService.currentlyLoggedInEmployee.reload()}: die Resource
   * dort haengt an einem {@code shareReplay()}-Observable, ein {@code reload()} bekaeme also denselben
   * gecachten (rollenlosen) Stand zurueck und der Knopf taete sichtbar nichts.
   */
  private readonly runde = signal(0)
  private readonly pruefung = rxResource({
    params: () => this.runde(),
    stream: () => this.http.get<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin`),
  })

  readonly pruefungLaeuft = computed(() => this.pruefung.isLoading())
  readonly automatikLaeuft = signal(true)

  private readonly zeitgeber = setInterval(() => {
    if (this.runde() >= MAX_AUTOMATISCHE_RUNDEN) {
      this.automatikLaeuft.set(false)
      return
    }
    this.runde.update((n) => n + 1)
  }, PRUEF_INTERVALL_MS)

  constructor() {
    inject(DestroyRef).onDestroy(() => clearInterval(this.zeitgeber))
  }

  /** Nur eine von Hand ausgeloeste Pruefung meldet ihr Ergebnis — sonst poppte alle 20 s ein Hinweis. */
  private readonly vonHandGeprueft = signal(false)

  logout() {
    this.authService.logout({ logoutParams: { returnTo: window.location.origin } })
  }

  jetztPruefen() {
    this.automatikLaeuft.set(true)
    this.vonHandGeprueft.set(true)
    this.runde.update((n) => n + 1)
  }

  /**
   * Ist die Rolle da, wird die Seite neu geladen statt nur der Zustand aktualisiert: der ganze
   * Anwendungsstart haengt an der Rolle (Seitenleiste, Stammdaten, `uiConfig`), und ein sauberer
   * Neustart ist hier gratis — auf diesem Bildschirm gibt es nichts zu verlieren.
   */
  private readonly freigeschaltetErkennen = effect(() => {
    if (!this.pruefung.hasValue() || this.pruefung.value().roles.length === 0) {
      return
    }
    window.location.reload()
  })

  /** „Es passiert nichts" ist die haeufigste Rueckmeldung — deshalb sagt der Knopf, was er gefunden hat. */
  private readonly ergebnisMelden = effect(() => {
    if (this.pruefung.isLoading() || !untracked(this.vonHandGeprueft)) {
      return
    }
    untracked(() => {
      this.vonHandGeprueft.set(false)
      this.snackbarService.showInfo('Dein Zugang ist noch nicht freigeschaltet.')
    })
  })
}
